import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps } from '@ui/src';
import { BalanceSheet, Category } from '../../types';
import { AccountType, ReportSheet, Total } from '../../../types';
import { accountsReportApi } from '../service';
import formReport from '../../../helpers/form-report';

export interface BalanceSheetState {
  itemsVisible: AccountType[];
  values?: ReportSheet[];
  checksum: number;
  totalAssets?: Total;
  totalLiabilities?: Total;
  formationTime?: {
    start?: number;
    end?: number;
    duration?: number;
  };
  lists: {
    categories: SelectItemProps[];
    groups: SelectItemProps[];
    primaries: SelectItemProps[];
  };
  accountsCategories?: Category[];
}

const initialState: BalanceSheetState = {
  values: undefined,
  itemsVisible: [],
  checksum: 0,
  totalAssets: undefined,
  totalLiabilities: undefined,
  formationTime: undefined,
  lists: {
    categories: [],
    groups: [],
    primaries: [],
  },
};

export const balanceSheetSlice = createSlice({
  name: 'balanceSheet',
  initialState,
  reducers: {
    changeVisibility: (state, action: PayloadAction<AccountType[]>) => {
      state.itemsVisible = action.payload;
    },
    setBSAccountsCategories: (state, action: PayloadAction<Category[]>) => {
      state.accountsCategories = action.payload;
    },
    clearBalanceSheetState: (state) => {
      state.values = undefined;
      state.checksum = 0;
      state.totalAssets = undefined;
      state.totalLiabilities = undefined;
      state.formationTime = undefined;
      state.lists = initialState.lists;
      state.itemsVisible = [AccountType.GROUP, AccountType.PRIMARY_ACCOUNT];
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        accountsReportApi.endpoints.getBalanceSheet.matchPending,
        (state) => {
          state.formationTime = {
            start: Date.now(),
            end: undefined,
            duration: undefined,
          };
        },
      )
      .addMatcher(
        accountsReportApi.endpoints.getBalanceSheet.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: BalanceSheet }> & {
            meta: { fulfilledTimeStamp: number };
          },
        ) => {
          const { checksum, totalAssets, totalLiabilities } =
            action.payload.data;

          const accountCategories = state.accountsCategories;

          const reportResult = formReport({
            ...action.payload.data,
            accountCategories: accountCategories as Category[],
          });

          state.lists = reportResult.lists;
          state.values = reportResult.sheet;

          state.checksum = checksum || 0;
          state.totalAssets = totalAssets || undefined;
          state.totalLiabilities = totalLiabilities || undefined;

          state.formationTime = {
            ...state.formationTime,
            end: action.meta.fulfilledTimeStamp,
            duration: Math.round(
              (action.meta.fulfilledTimeStamp -
                (state.formationTime?.start as number)) /
                1000,
            ),
          };
        },
      );
  },
});

export const {
  changeVisibility,
  clearBalanceSheetState,
  setBSAccountsCategories,
} = balanceSheetSlice.actions;

export default balanceSheetSlice.reducer;
